export const APPS = [
  {
    slug: "maven",
    name: "Maven",
    tagline: "The AI that takes action.",
    description:
      "Maven is a voice-first AI assistant that connects to the services you authorize and takes real actions on your behalf — sending emails, creating calendar events, querying data, and more — through natural conversation. You think it. Maven does it.",
    icon: require("assets/img/edge/maven-icon.png"),
    screenshots: [require("assets/img/edge/maven-screenshot.png")],
    badge: null,
    playStoreUrl: "https://play.google.com/store/apps/details?id=ai.generals.app",
    appStoreUrl: "https://apps.apple.com/us/app/maven-voice-assistant/id6784575363",
    comingSoon: false,
    support: {
      email: "support@generals.ai",
      privacyEmail: "support@generals.ai",
      responseTime: "2 business days",
      faq: [
        {
          q: "What is Maven?",
          a: "Maven is a voice-first AI assistant that connects to third-party services you authorize and takes real actions on your behalf — sending emails, creating calendar events, querying databases, and more — through natural conversation.",
        },
        {
          q: "How do I cancel my subscription?",
          a: "Subscriptions are managed through your App Store or Google Play account, not inside the app.\n\niPhone/iPad: Settings → your name → Subscriptions → Maven\nAndroid: Play Store → Profile → Payments & subscriptions → Subscriptions → Maven",
        },
        {
          q: "How do I delete my account and data?",
          a: "Open Settings → Account → Delete Account. This permanently removes all associated data within 30 days. For any other data request, email support@generals.ai.",
        },
        {
          q: "How do I disconnect a service Maven has access to?",
          a: "Email support@generals.ai to revoke Maven's access to any connected service. Credentials are purged within 30 days of a confirmed revocation request.",
        },
        {
          q: "Is Maven free?",
          a: "Maven is free to use and includes five AI personas: Kairos, Maven, Ellis, Winston, and Alfred. Additional personas require a paid subscription, available via in-app purchase on iOS and Android.",
        },
      ],
    },
    privacy: {
      effectiveDate: "June 6, 2026",
      sections: [
        {
          title: "Who we are",
          body: "Maven (\"Maven,\" \"we,\" \"us,\" or \"our\") is a product of Edge Ventures, Inc., a California corporation. This Privacy Policy explains how we collect, use, store, and share information when you use the Maven mobile application and related services (collectively, the \"Service\"). By using the Service, you agree to this policy.",
        },
        {
          title: "What we collect",
          body: "We collect the following categories of information:\n\nAccount information:\n• Email address and password (hashed, never stored in plaintext)\n• Display name, if provided\n• Subscription status and in-app purchase receipts\n\nVoice and conversation data:\n• Real-time audio streamed from your device microphone during active voice sessions. Audio is not stored after transcription is complete.\n• Conversation transcripts — the text record of what you said and what Maven responded.\n• Session metadata: timestamp, duration, persona used, and device identifier.\n\nThird-party connection credentials:\n• OAuth tokens, API keys, or other credentials for services you explicitly authorize Maven to access (e.g., Google Calendar, Gmail, Notion).\n• These credentials are encrypted at the application layer using AES-256 before being written to the database and are never transmitted outside of the operation you authorized.\n\nAction logs:\n• A record of actions Maven took on your behalf (e.g., \"Sent email to Jane Doe,\" \"Created calendar event\").\n\nTechnical and device data:\n• Device type, operating system version, and app version\n• IP address and approximate location (country/region) for fraud prevention\n• Crash reports and performance diagnostics",
        },
        {
          title: "How we use your data",
          body: "• Delivering the Service — processing your voice, routing requests to third-party services you have connected, and returning responses.\n• Executing autonomous actions — your conversation transcripts and action context are passed to Maven's Enclave agent to perform tasks on your behalf.\n• Account management — authentication, subscription billing, and customer support.\n• Safety and fraud prevention — detecting abuse, unauthorized access, and policy violations.\n• Session memory and context — Maven retains a summary of past sessions to provide continuity across conversations. This memory is used solely to make future interactions more relevant to you; it is never used to train AI models.",
        },
        {
          title: "Autonomous agent actions (Enclave)",
          body: "Maven uses an agentic execution environment called Enclave that can take real actions in third-party systems on your behalf — sending emails, creating calendar events, querying databases, and more. When you authorize a connection, you are granting Enclave permission to interact with that service within the scope of your voice requests.\n\nTo perform these actions, the Enclave processes your credentials and relevant conversation context. This processing occurs on Edge Ventures' private infrastructure and is not shared with third parties beyond what is necessary to complete the requested action.\n\nYou can revoke access to any connected service at any time by contacting support@generals.ai. Credentials are purged within 30 days of a confirmed revocation request.",
        },
        {
          title: "Data retention",
          body: "• Voice audio is not retained after transcription. Raw audio is discarded within seconds of the session ending.\n• Conversation transcripts and action logs are retained until you delete your account, or until you submit a deletion request to support@generals.ai.\n• Third-party credentials are deleted immediately when you remove a connection.\n• Account data is deleted within 30 days of account deletion.\n• Crash reports and diagnostic data are retained for 90 days.\n• Session memory (context summaries used to personalize future conversations) is retained until you delete your account or request deletion.",
        },
        {
          title: "Third-party service providers",
          body: "Maven routes data through the following sub-processors as part of normal operation. Each receives only the data necessary for its function:\n\n• Deepgram — receives your audio stream for speech-to-text transcription. Audio is processed in real time and not retained by Deepgram after transcription.\n• Anthropic (Claude) — receives conversation transcript and context for AI reasoning. Subject to Anthropic's Privacy Policy at anthropic.com/legal/privacy.\n• Cartesia — receives text output for text-to-speech synthesis. No personal data beyond the response text is transmitted.\n• LiveKit — provides real-time WebRTC audio transport between your device and our servers. LiveKit does not store audio.\n\nWe do not sell, rent, or share your personal data with third parties for advertising or commercial purposes.",
        },
        {
          title: "Security",
          body: "All data is encrypted in transit (TLS 1.2+) and at rest (AES-256). Third-party credentials are additionally encrypted at the application layer before storage. Our infrastructure runs on a private Kubernetes cluster with network-level access controls. We conduct periodic security reviews and will notify affected users of any material data breach as required by applicable law.",
        },
        {
          title: "Your rights",
          body: "Depending on your jurisdiction, you may have the right to access, correct, export, or delete your personal data. Maven provides the following controls:\n\n• Remove connections — to revoke Maven's access to a connected third-party service, email support@generals.ai; credentials are purged within 30 days.\n• Delete account — Settings → Account → Delete Account permanently removes all associated data within 30 days.\n\nTo request deletion of conversation history, action logs, or session memory, to export your data, or for any other data request, contact us at support@generals.ai. We will respond within 30 days.",
        },
        {
          title: "Children's privacy",
          body: "Maven is not directed to individuals under the age of 18. We do not knowingly collect personal data from minors. If we learn that we have collected data from a user under 18, we will delete that data promptly. If you believe a minor has registered, please contact support@generals.ai.",
        },
        {
          title: "International transfers",
          body: "Edge Ventures is based in the United States. If you access Maven from outside the US, your data will be transferred to and processed in the US. For users in the EEA, UK, or Switzerland, we rely on standard contractual clauses as the legal basis for such transfers.",
        },
        {
          title: "Changes to this policy",
          body: "We may update this Privacy Policy from time to time. We will notify you of material changes via in-app notification or email at least 14 days before the change takes effect. Continued use of Maven after the effective date constitutes acceptance of the updated policy.",
        },
        {
          title: "Contact",
          body: "Privacy questions or data requests: support@generals.ai\nEdge Ventures, Inc. · Los Angeles, CA",
        },
      ],
    },
    terms: {
      effectiveDate: "June 6, 2026",
      sections: [
        {
          title: "Agreement",
          body: "These Terms of Service (\"Terms\") form a binding agreement between you and Edge Ventures, Inc. (\"Edge Ventures,\" \"we,\" \"us,\" or \"our\") governing your access to and use of Maven, including the Maven mobile application, web interface, and all related services (collectively, the \"Service\"). By creating an account or using the Service, you agree to these Terms in full. If you do not agree, do not use the Service.",
        },
        {
          title: "Eligibility",
          body: "• You must be at least 18 years old to use Maven.\n• You must have the legal capacity to enter into a binding contract in your jurisdiction.\n• You may maintain only one account per person. Duplicate accounts may be suspended without notice.\n• If you are using Maven on behalf of an organization, you represent that you have authority to bind that organization to these Terms.",
        },
        {
          title: "Service description",
          body: "Maven is a voice-first AI assistant that connects to third-party services you authorize and takes actions on your behalf through natural conversation. The Service is currently provided as beta software. Features may be incomplete, subject to change, and may not perform as expected.\n\nMaven is powered by large language models and AI reasoning systems. Responses and actions generated by Maven are probabilistic outputs — they are not guaranteed to be accurate, complete, or appropriate for your specific situation. You are responsible for reviewing any material action Maven proposes before relying on it.\n\nThe Service is licensed to you, not sold. Your account grants you a limited, non-exclusive, non-transferable, revocable right to access and use the Service during your subscription period, similar to leasing access to a cloud platform. You do not acquire ownership of any underlying software, infrastructure, or AI models. Edge Ventures reserves all rights not expressly granted.",
        },
        {
          title: "Authorized third-party actions",
          body: "A core feature of Maven is its ability to take real actions in third-party services — sending emails, creating calendar events, querying databases, submitting forms, and more — on your behalf via Maven's Enclave agent. By connecting a third-party service, you authorize Maven to interact with that service within the scope of your voice requests.\n\nActions may be executed autonomously without a confirmation step. While the Enclave may pause to request your confirmation for certain sensitive actions, this is not guaranteed for every operation. By using the Service, you accept that Maven may act immediately on your spoken intent. Edge Ventures is not liable for errors, unintended consequences, data loss, or third-party service disruptions arising from actions Maven takes on your behalf — including but not limited to emails sent in error, calendar events created or deleted, files modified or removed, and queries executed against databases.\n\nYou may revoke Maven's access to any connected service at any time by contacting support@generals.ai. Credentials are purged within 30 days of a confirmed revocation request.",
        },
        {
          title: "Account security",
          body: "You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account. You agree to notify us immediately at support@generals.ai if you suspect unauthorized access. Edge Ventures is not liable for losses resulting from unauthorized use of your account.",
        },
        {
          title: "Shared responsibility for security",
          body: "Maven operates on a shared responsibility model, similar to enterprise cloud platforms such as AWS or Microsoft Azure. Edge Ventures is responsible for the security of the platform — the infrastructure, servers, and systems that run the Service. You are responsible for the security of your configuration — including which services you connect, what permissions you grant, and how you use Maven's agentic capabilities.\n\nWhen you connect a third-party service, you are granting Maven permission to act within that service on your behalf. The scope of that access is determined entirely by you. Edge Ventures does not review, audit, restrict, or limit the permissions you choose to authorize. If you grant broad or administrative access — such as full mailbox access, database write permissions, or file system access — Maven will operate within that scope.\n\nYou accept full responsibility for the consequences of your configuration choices, including:\n• Data exposure or leakage resulting from over-permissioned connections you authorized.\n• Unauthorized access to connected services resulting from compromised account credentials.\n• Actions taken by Maven within the scope of access you provided, including irreversible actions such as deleted files, sent messages, or modified records.\n• Security incidents in third-party services that stem from the permissions you granted, misconfigured OAuth scopes, or API keys left active after they are no longer needed.\n\nUse caution. Grant Maven only the access it needs for the tasks you intend. Revoke access to services you no longer use. Treat Maven like any other system you give credentials to — the risk profile of what you connect is yours to manage.",
        },
        {
          title: "Acceptable use",
          body: "You agree to use Maven only for lawful purposes and consistent with these Terms. You agree not to:\n• Use Maven to access systems, accounts, or data you are not authorized to access.\n• Transmit, generate, or solicit content that is illegal, abusive, threatening, defamatory, or that infringes third-party intellectual property rights.\n• Use Maven to develop competing AI products or services, or to extract data for training external AI models.\n• Attempt to reverse engineer, decompile, or circumvent any security or access controls of the Service.\n• Scrape, crawl, or extract data from the Service by automated means without written permission.\n• Use Maven to facilitate spam, phishing, fraud, or any deceptive practice.\n• Impersonate any person or entity, or misrepresent your affiliation with any person or entity.\n• Use Maven in any way that violates Anthropic's Usage Policy, which governs the underlying AI models.",
        },
        {
          title: "AI limitations and no-reliance",
          body: "Maven's outputs — including transcriptions, responses, summaries, and actions — are generated by AI and may contain errors, omissions, or outdated information. You acknowledge that:\n• Maven is not a substitute for professional legal, medical, financial, or other expert advice.\n• You should independently verify any information or action before relying on it for important decisions.\n• Maven may misinterpret voice input, especially in noisy environments or with accents and dialects.\n• AI-generated outputs may reflect biases or inaccuracies inherent in underlying models.",
        },
        {
          title: "Free and paid tiers",
          body: "Maven is available at no charge and includes five AI personas: Kairos, Maven, Ellis, Winston, and Alfred. Additional personas require a paid subscription, available via in-app purchase on iOS and Android. Pricing, billing frequency, and included personas are displayed in the app at the time of purchase.\n\nSubscriptions automatically renew unless cancelled at least 24 hours before the end of the current billing period. You can manage and cancel your subscription in your device's app store account settings. Edge Ventures does not process subscription billing directly — all billing is handled by Apple App Store or Google Play.\n\nEdge Ventures reserves the right to modify pricing with at least 30 days' notice. Continued use of a paid tier after the notice period constitutes acceptance of the new pricing.",
        },
        {
          title: "Your content and data",
          body: "You retain ownership of all content you provide to Maven, including voice inputs, conversation history, and any data from connected third-party services. By using the Service, you grant Edge Ventures a limited, non-exclusive, worldwide license to process your content solely to operate and provide the Service.\n\nEdge Ventures does not sell your content to third parties and does not use your data to train AI models. Maven retains session memory solely to provide continuity across your conversations. See our Privacy Policy for full details.",
        },
        {
          title: "Intellectual property",
          body: "The Service, including its software, design, trademarks, and all content created by Edge Ventures, is the property of Edge Ventures and is protected by applicable intellectual property laws. Nothing in these Terms grants you any rights to our intellectual property beyond the limited right to use the Service as described herein.",
        },
        {
          title: "Disclaimer of warranties",
          body: "THE SERVICE IS PROVIDED \"AS IS\" AND \"AS AVAILABLE\" WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, AND NON-INFRINGEMENT. EDGE VENTURES DOES NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF HARMFUL COMPONENTS. AS BETA SOFTWARE, THE SERVICE MAY HAVE BUGS, UNEXPECTED BEHAVIOR, OR FEATURE GAPS.",
        },
        {
          title: "Limitation of liability",
          body: "TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, EDGE VENTURES AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND SERVICE PROVIDERS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF OR INABILITY TO USE THE SERVICE. THIS INCLUDES, WITHOUT LIMITATION:\n• Errors in actions taken by Maven on your behalf in third-party services\n• Loss or corruption of data\n• Unauthorized access to your account or connected services\n• Service interruptions or downtime\n• Inaccuracies in AI-generated responses\n\nIN NO EVENT SHALL EDGE VENTURES' TOTAL LIABILITY TO YOU EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID FOR THE SERVICE IN THE TWELVE MONTHS PRECEDING THE CLAIM OR (B) ONE HUNDRED DOLLARS ($100 USD).",
        },
        {
          title: "Indemnification",
          body: "You agree to indemnify, defend, and hold harmless Edge Ventures and its affiliates, officers, directors, employees, and agents from any claims, liabilities, damages, losses, and costs (including reasonable legal fees) arising from: (a) your use of the Service; (b) your violation of these Terms; (c) any actions Maven takes in third-party services on your behalf; or (d) your violation of any third party's rights.",
        },
        {
          title: "Termination",
          body: "You may delete your account at any time via Settings → Account → Delete Account. Deleting your account terminates these Terms and triggers deletion of your data per our Privacy Policy.\n\nEdge Ventures may suspend or terminate your account immediately, without notice, for violation of these Terms, misuse of the Service, or conduct deemed harmful to other users or the platform. Upon termination, your right to use the Service ends immediately. The disclaimer, limitation of liability, indemnification, and governing law sections survive termination.",
        },
        {
          title: "Changes to these Terms",
          body: "We may update these Terms from time to time. We will notify you of material changes via in-app notification or email at least 14 days before the change takes effect. Continued use of the Service after the effective date constitutes acceptance of the updated Terms. If you do not agree to the updated Terms, you must stop using the Service and delete your account.",
        },
        {
          title: "Governing law and disputes",
          body: "These Terms are governed by the laws of the State of California, without regard to conflict of law principles. Any dispute arising from these Terms or your use of the Service shall be subject to the exclusive jurisdiction of the state and federal courts located in Los Angeles County, California, and you consent to personal jurisdiction in those courts.",
        },
        {
          title: "Contact",
          body: "Questions about these Terms: support@generals.ai\nEdge Ventures, Inc. · Los Angeles, CA",
        },
      ],
    },
    dataDeletion: {
      email: "support@generals.ai",
      mailtoSubject: "Data Deletion Request",
      mailtoBody:
        "I am requesting deletion of my Maven account and all associated personal data.\n\n" +
        "Account email (the email you signed up with): \n" +
        "Display name (if any): \n\n" +
        "I understand this will permanently delete my account, conversation history, " +
        "connected-service credentials, and session memory, and that this action cannot be undone.",
      intro:
        "You can request deletion of your Maven account and all personal data associated with it at any time. You can do it yourself right inside the app, or request it by email — whichever you prefer.",
      appSteps:
        "You can delete your account and all of your data directly from the Maven app — it's quick and easy. Just remember that deletion is permanent and can't be undone, so make sure it's really what you want before you confirm.\n\nOpen Settings → Privacy & Security, then under Danger Zone tap Delete Account and confirm.",
      screenshots: [
        {
          src: require("assets/img/edge/maven-delete-1.jpg"),
          caption: "1. Settings → Privacy & Security → Delete Account",
        },
        {
          src: require("assets/img/edge/maven-delete-2.jpg"),
          caption: "2. Confirm — this cannot be undone",
        },
      ],
      whatGetsDeleted: [
        "Your account and profile information (email, display name, subscription status)",
        "Your conversation transcripts and session memory",
        "Credentials for any third-party services you connected (OAuth tokens, API keys)",
        "Action logs and session metadata tied to your account",
      ],
      timeline:
        "We will confirm receipt of your request and complete deletion within 30 days. Some information may be retained for a limited period where required by law (for example, transaction records for tax and accounting purposes), after which it is also deleted. Once deletion is complete, the action cannot be undone.",
    },
  },
  {
    slug: "harness-gym",
    name: "Harness Gym",
    tagline: "Prove your AI agent can log in to anything.",
    description:
      "Harness Gym is a standalone testbed — a spin-off of the Maven project — where AI agents prove they can authenticate against different sources and credential schemes. Walk every station, complete each login, and get scored on how many of the 12 stations your agent can clear.",
    icon: null,
    screenshots: [require("assets/img/edge/harness-gym.png")],
    externalUrl: "https://oauth-test.edgeventures.com",
    badge: "Live",
    playStoreUrl: null,
    appStoreUrl: null,
    comingSoon: false,
  },
  {
    slug: "doodletoon",
    name: "DoodleToon",
    tagline: "Turn your doodles into animated cartoons with AI",
    description:
      "DoodleToon uses AI to transform hand-drawn sketches and photos into magical animated cartoon videos. Draw it, snap it, animate it.",
    icon: require("assets/img/edge/doodletoon-icon.png"),
    screenshots: [
      "https://is1-ssl.mzstatic.com/image/thumb/PurpleSource211/v4/fa/30/75/fa307578-3f0c-f7e5-889e-05973d45323e/image4.png/314x680bb.webp",
    ],
    playStoreUrl:
      "https://play.google.com/store/apps/details?id=com.doodletoon.app",
    appStoreUrl: "https://apps.apple.com/us/app/doodletoon/id6756547957",
    comingSoon: false,
    support: {
      email: "support@doodletoon.ai",
      privacyEmail: "privacy@doodletoon.ai",
      responseTime: "24–48 hours",
      faq: [
        {
          q: "How long does it take to create a video?",
          a: "Most videos are generated in 30–60 seconds depending on server demand and the complexity of your artwork.",
        },
        {
          q: "Why did my video fail to generate?",
          a: "Videos can fail due to low image quality, high server demand, or safety filters detecting inappropriate content. Try again with a clearer image or a different prompt.",
        },
        {
          q: "How do credits work?",
          a: "Each video generation uses one credit. Credits are purchased inside the app and never expire.",
        },
        {
          q: "Can I get a refund?",
          a: "If a video fails to generate, credits are automatically refunded. For other refund requests, please contact your app store (Apple App Store or Google Play) directly.",
        },
        {
          q: "How do I delete my account and data?",
          a: "Email support@doodletoon.ai with the subject 'Delete My Account'. Your data will be permanently removed within 48 hours.",
        },
        {
          q: "Is my child's artwork safe?",
          a: "We take privacy seriously. Artwork and prompts are used only to generate your video and are never shared with third parties or used for advertising.",
        },
      ],
    },
    privacy: {
      effectiveDate: "January 9, 2026",
      lastUpdated: "February 8, 2026",
      sections: [
        {
          title: "COPPA Compliance",
          body: "DoodleToon is designed for families and complies with the Children's Online Privacy Protection Act (COPPA). We require verifiable parental consent before collecting any data from users under 13. Parents or guardians must create and control all accounts.",
        },
        {
          title: "Information We Collect",
          body: "With parental consent, we collect: account information (email address, username, password), artwork photos and text prompts you submit, and generated animated content. We do not collect audio, advertising identifiers, precise location/GPS, device identifiers, or social media information.",
        },
        {
          title: "How We Use Your Information",
          body: "Your information is used solely to create animated videos, manage your account, improve our AI technology, and send service notifications. We never use your data for marketing, advertising, or commercial purposes beyond core services.",
        },
        {
          title: "Third-Party Services",
          body: "We use Amazon Web Services for data storage, Google Veo 2 for AI video processing, and payment processors for in-app purchases. Each partner is bound by data protection agreements and may not use your data for their own purposes.",
        },
        {
          title: "Data Security",
          body: "All data is encrypted in transit and at rest. We use AWS infrastructure with industry-standard security protocols, conduct regular security audits, and limit employee access to personal data on a need-to-know basis.",
        },
        {
          title: "Data Retention & Deletion",
          body: "You may request deletion of your data at any time by emailing privacy@doodletoon.ai. We will permanently remove all associated data within 30 days and send email confirmation to the parent account.",
        },
        {
          title: "International Data Transfer",
          body: "All data is processed and stored in the United States. By using DoodleToon, you consent to transfer of your information to the United States, where privacy laws may differ from your country of residence.",
        },
        {
          title: "Parental Rights",
          body: "Parents and guardians have the right to: (1) review the personal information collected about their child, (2) request deletion of that information, (3) refuse further collection or use, and (4) export their data. Contact privacy@doodletoon.ai to exercise these rights. We respond within 10 business days.",
        },
        {
          title: "No Data Sales or Marketing",
          body: "We never sell personal information. We do not use behavioral advertising, share data for marketing purposes, or allow third parties to use your information for their own commercial interests.",
        },
        {
          title: "Policy Changes",
          body: "We will notify parents via email before making any material changes to this privacy policy. Continued use after notification constitutes acceptance of the updated policy.",
        },
        {
          title: "Contact Us",
          body: "Privacy questions: privacy@doodletoon.ai\nGeneral support: support@doodletoon.ai\nWe respond to all privacy requests within 10 business days.",
        },
      ],
    },
    terms: {
      effectiveDate: "January 9, 2026",
      sections: [
        {
          title: "Acceptance of Terms",
          body: "By downloading, installing, or using DoodleToon, you agree to these Terms and Conditions. If you are under 13, a parent or guardian must agree on your behalf. If you do not agree to these terms, do not use the app.",
        },
        {
          title: "License",
          body: "Edge Ventures grants you a limited, non-exclusive, non-transferable, revocable license to use DoodleToon for personal, non-commercial purposes on devices you own or control. This license does not include the right to resell, distribute, or create derivative works based on the app.",
        },
        {
          title: "Credits and Purchases",
          body: "DoodleToon uses a credit system for video generation. Credits are purchased through the app store and are non-refundable except where required by applicable law. Failed generations will automatically receive a credit refund. Credits do not expire and have no cash value.",
        },
        {
          title: "Content and Conduct",
          body: "You agree not to submit content that is illegal, harmful, threatening, abusive, defamatory, obscene, or otherwise objectionable. You agree not to attempt to reverse-engineer, hack, or interfere with the app or its servers. Edge Ventures reserves the right to remove content or terminate accounts that violate these rules.",
        },
        {
          title: "Intellectual Property",
          body: "The DoodleToon app, including its AI models, interface, and branding, is owned by Edge Ventures. You retain ownership of the original artwork you submit. Videos generated by DoodleToon may be used for personal, non-commercial purposes. Commercial licensing inquiries should be directed to support@doodletoon.ai.",
        },
        {
          title: "Disclaimers",
          body: "DoodleToon is provided 'as is' without warranties of any kind, express or implied. We do not guarantee that the service will be uninterrupted, error-free, or that generated videos will meet your expectations. Use of AI-generated content is at your own discretion.",
        },
        {
          title: "Limitation of Liability",
          body: "To the maximum extent permitted by law, Edge Ventures shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of DoodleToon. Our total liability to you for any claims arising from use of the app shall not exceed the amount you paid for credits in the 12 months preceding the claim.",
        },
        {
          title: "Termination",
          body: "We may suspend or terminate your access to DoodleToon at any time for violation of these terms. You may terminate your account at any time by contacting support@doodletoon.ai. Upon termination, your license to use the app ends immediately.",
        },
        {
          title: "Governing Law",
          body: "These Terms are governed by the laws of the State of California, United States, without regard to conflict of law provisions. Any disputes shall be resolved in the courts of Los Angeles County, California.",
        },
        {
          title: "Contact",
          body: "Questions about these Terms: support@doodletoon.ai\nEdge Ventures INC · Los Angeles, CA",
        },
      ],
    },
  },
  {
    slug: "last-episode",
    name: "Last Episode",
    tagline: "Track your episodes and reveal your patterns",
    description:
      "Last Episode helps you log mood and health episodes, then surfaces the patterns behind them — when they cluster, how intense they are, and what triggers them. An AI assistant helps you make sense of your data over time.",
    icon: require("assets/img/edge/last-episode-icon.png"),
    screenshots: [require("assets/img/edge/last-episode-screenshot.png")],
    playStoreUrl: "https://play.google.com/store/apps/details?id=ai.lastepisode.app",
    appStoreUrl: "https://apps.apple.com/us/app/last-episode/id6768906712",
    comingSoon: false,
    support: {
      email: "support@lastepisode.ai",
      privacyEmail: "support@lastepisode.ai",
      responseTime: "2 business days",
      faq: [
        {
          q: "My data disappeared after reinstalling the app.",
          a: "Episode data is stored locally on your device. Uninstalling the app removes that data. If you had an account and sync enabled, your episodes can be restored by signing back in.",
        },
        {
          q: "The AI assistant isn't responding.",
          a: "Check your internet connection — the assistant requires a live connection to generate responses. If the problem persists, try closing and reopening the app.",
        },
        {
          q: "How do I cancel my subscription?",
          a: "Subscriptions are managed through your App Store or Google Play account, not through the app itself.\n\niPhone/iPad: Settings → your name → Subscriptions → Last Episode\nAndroid: Play Store → Profile → Payments & subscriptions → Subscriptions → Last Episode",
        },
        {
          q: "How do I delete all my data?",
          a: "Go to Settings → Delete all my data. This removes everything stored on your device immediately. To delete your account and any server-side data, email us at support@lastepisode.ai.",
        },
        {
          q: "I found a bug.",
          a: "Email support@lastepisode.ai with a description of what happened and what device and OS version you're on. Screenshots help.",
        },
      ],
    },
    privacy: {
      effectiveDate: "May 2026",
      lastUpdated: "May 2026",
      sections: [
        {
          title: "The short version",
          body: "Your episode logs stay on your device. We don't sell your data. We don't share it with advertisers. The only time your data leaves your phone is if you use the AI assistant or create an account.",
        },
        {
          title: "Episode logs, tags, and notes",
          body: "Stored locally on your device using SQLite. Never transmitted to our servers unless you sign in and sync. You can delete everything at any time from Settings → Delete all my data.",
        },
        {
          title: "Account information (optional)",
          body: "If you create an account, we store your email address and a hashed password to identify you across devices. No account is required to use the app.",
        },
        {
          title: "AI assistant usage",
          body: "When you send a message to the assistant, your recent episode history is transmitted to a third-party AI service to generate a response. This data is not stored by that service or used to train models. If you prefer to keep everything local, you can disable the assistant in Settings.",
        },
        {
          title: "Subscription status",
          body: "If you upgrade to Last Episode Pro, your purchase is managed by RevenueCat. We receive your subscription status only — no payment details or card numbers.",
        },
        {
          title: "What we don't do",
          body: "We do not sell your data. We do not show you ads. We do not share your health information with advertisers or data brokers. We do not use your episode data to train AI models.",
        },
        {
          title: "Data deletion",
          body: "You can delete all your local data from Settings → Delete all my data. To delete your account and any server-side data, email us at support@lastepisode.ai.",
        },
        {
          title: "Contact",
          body: "Questions? support@lastepisode.ai",
        },
      ],
    },
    terms: {
      effectiveDate: "May 2026",
      lastUpdated: "May 2026",
      sections: [
        {
          title: "The short version",
          body: "This app helps you track personal habits and episodes. It is not medical advice. Use it honestly, don't abuse it, and understand that we provide it as-is.",
        },
        {
          title: "Who can use Last Episode",
          body: "You must be 18 or older to use this app. By using it, you agree to these terms.",
        },
        {
          title: "What Last Episode is (and isn't)",
          body: "Last Episode is a personal tracking tool. It is not a medical device, a clinical service, or a substitute for professional help. Nothing in the app — including responses from the AI assistant — constitutes medical, psychological, or therapeutic advice.\n\nIf you are in crisis, please contact a qualified professional or a crisis line in your country.",
        },
        {
          title: "Your data",
          body: "Your episode logs belong to you. We don't claim ownership over anything you record. See our Privacy Policy for details on how data is stored and handled.",
        },
        {
          title: "Subscriptions",
          body: "Last Episode Pro is a paid subscription managed through the App Store or Google Play. Subscriptions renew automatically unless cancelled at least 24 hours before the renewal date. You can manage or cancel your subscription in your device's account settings. We do not offer refunds outside of what Apple or Google provide through their standard policies.",
        },
        {
          title: "Acceptable use",
          body: "You agree not to reverse-engineer, scrape, or abuse the app or its backend services. One account per person.",
        },
        {
          title: "Disclaimer",
          body: "Last Episode is provided as-is. We make no guarantees that it will be available at all times or that it will work perfectly on every device. We are not liable for any decisions you make based on information in the app.",
        },
        {
          title: "Changes to these terms",
          body: "We may update these terms occasionally. If the changes are significant, we'll note it here with a new date. Continued use of the app means you accept the updated terms.",
        },
        {
          title: "Contact",
          body: "Questions? support@lastepisode.ai",
        },
      ],
    },
  },
  {
    slug: "skillsculptor",
    name: "SkillSculptor.ai",
    tagline: "Stop sending the same resume to every job.",
    description:
      "Paste your history once, then point it at a posting. SkillSculptor writes a resume tailored to that job — and a new one for the next, in about a minute each. No account, no card, and four designs with no watermark.",
    icon: null,
    screenshots: [require("assets/img/edge/skillsculptor.png")],
    externalUrl: "https://skillsculptor.ai/",
    badge: "Live",
    playStoreUrl: null,
    appStoreUrl: null,
    comingSoon: false,
  },
  {
    slug: "mermaidstage",
    name: "MermaidStage",
    tagline: "Give your architecture a story.",
    description:
      "Turn a Mermaid diagram into a narrated 3D tour of your architecture. Play the story, pause at the interesting part, and explore the system. An API and MCP first approach for people, agents, and the tools you already use.",
    icon: null,
    screenshots: [require("assets/img/edge/mermaidstage.png")],
    externalUrl: "https://mermaidstage.com/",
    badge: "Live",
    playStoreUrl: null,
    appStoreUrl: null,
    comingSoon: false,
  },
  {
    slug: "violet",
    name: "Violet",
    tagline: "AI product management, in one workspace.",
    description:
      "Violet keeps a product's roadmap, features, projects, notes and decisions in one workspace, with an AI assistant that can create and change work in plain language.",
    icon: null,
    screenshots: [require("assets/img/edge/violet.png")],
    externalUrl: "https://violetvp.com/",
    badge: "Live",
    playStoreUrl: null,
    appStoreUrl: null,
    comingSoon: false,
  },
  {
    slug: "placeholder-1",
    name: "Coming Soon",
    tagline: "Another Edge Ventures app on the way",
    description: "Stay tuned for our next product.",
    icon: null,
    screenshots: [],
    playStoreUrl: null,
    appStoreUrl: null,
    comingSoon: true,
    support: { email: "support@edgeventures.ai", faq: [] },
    privacy: { effectiveDate: "", sections: [] },
    terms: { effectiveDate: "", sections: [] },
  },
];

export function getApp(slug) {
  return APPS.find((a) => a.slug === slug) || null;
}
